import type { Dayjs } from '@/config/dayjs';
import type { WealthTrendSeries } from '@/interfaces/wealthReportedTrend';
import { pointDay, type WealthTrendWindow } from './wealthTrendMath';

/** One series laid out against the chart's shared days. */
export interface WealthTrendLine {
  /** The API's series id: 1 managed, 2 held away. */
  id: number;
  name: string;
  /** One value per day in `WealthTrendChart.days`; null where the series has none. */
  values: (number | null)[];
}

/** The series aligned onto one time axis. */
export interface WealthTrendChart {
  days: Dayjs[];
  lines: WealthTrendLine[];
}

/** A point on the chart: which line, and which of the shared days. */
export interface WealthTrendPointRef {
  seriesId: number;
  dayIndex: number;
}

/** The managed series; the details endpoint's `isHome` asks for this one. */
export const MANAGED_SERIES_ID = 1;

/**
 * Lays the series out on the days any of them has a point, in order, so both
 * lines share one x axis. A day one series lacks is null for that line rather
 * than a drop to zero; the chart draws across it to the line's next point.
 */
export function buildWealthTrendChart(
  series: readonly WealthTrendSeries[],
): WealthTrendChart {
  const byKey = new Map<number, Dayjs>();
  const valuesBySeries = series.map((line) => {
    const values = new Map<number, number>();
    line.data.forEach((point) => {
      const day = pointDay(point);
      if (!day) return;
      byKey.set(day.valueOf(), day);
      values.set(day.valueOf(), point.Value);
    });
    return values;
  });

  const days = [...byKey.values()].sort((a, b) => a.valueOf() - b.valueOf());
  const lines = series.map((line, index) => ({
    id: line.id,
    name: line.name,
    values: days.map((day) => valuesBySeries[index].get(day.valueOf()) ?? null),
  }));

  return { days, lines };
}

/** Whether there is anything to draw. */
export function hasTrendPoints(chart: WealthTrendChart): boolean {
  return chart.lines.some((line) =>
    line.values.some((value) => value !== null),
  );
}

/**
 * How far past a drag's edge a day still counts as inside it. A drag that
 * starts or ends on a day's point lands a few pixels either side of it, which
 * on a long range is minutes past midnight, so that day was dropped from Net
 * Change and the date box while the chart still drew it.
 */
const ZOOM_EDGE_SLACK_MS = 6 * 60 * 60 * 1000;

/**
 * The days a zoom covers, or null when the chart is not zoomed.
 *
 * The chart's start and end percentages are of its time axis, which runs
 * from the first day to the last, not of the list of days: data skips
 * weekends and holidays, so a day's place in the list is not its place in
 * time. The percentages become times on that axis, and the window is the
 * first and last days inside them -- the points the zoomed chart shows. A drag
 * too narrow to hold a day keeps the days either side of it.
 */
export function zoomWindow(
  days: readonly Dayjs[],
  zoom: { start: number; end: number } | null,
): WealthTrendWindow | null {
  if (!zoom || days.length === 0) return null;
  if (zoom.start <= 0 && zoom.end >= 100) return null;

  const first = days[0].valueOf();
  const span = days[days.length - 1].valueOf() - first;
  const from = first + (span * zoom.start) / 100 - ZOOM_EDGE_SLACK_MS;
  const to = first + (span * zoom.end) / 100 + ZOOM_EDGE_SLACK_MS;

  const inside = days.filter(
    (day) => day.valueOf() >= from && day.valueOf() <= to,
  );
  if (inside.length > 0) {
    return { start: inside[0], end: inside[inside.length - 1] };
  }
  const after = days.findIndex((day) => day.valueOf() > to);
  const end = after === -1 ? days.length - 1 : after;
  return { start: days[Math.max(0, end - 1)], end: days[end] };
}

/** The value at a point, or null when the line has none that day. */
export function valueAt(
  chart: WealthTrendChart,
  ref: WealthTrendPointRef,
): number | null {
  const line = chart.lines.find((candidate) => candidate.id === ref.seriesId);
  return line?.values[ref.dayIndex] ?? null;
}

/**
 * Past this many days the chart draws lines without a dot per day: a year of
 * dots overlaps into a blur. Legacy's chart dropped its markers the same way,
 * showing one only for the point under the pointer.
 */
export const DENSE_TREND_DAYS = 62;

/** Whether the range is long enough to draw lines without per-day dots. */
export function isDenseTrend(chart: WealthTrendChart): boolean {
  return chart.days.length > DENSE_TREND_DAYS;
}

/**
 * The line closest to the pointer on a day, for a chart drawn without dots,
 * where there is no dot to say which line was meant. `toPixel` maps a value to
 * its height on screen; lines with no value that day are passed over.
 */
export function nearestLine(
  chart: WealthTrendChart,
  dayIndex: number,
  pointerY: number,
  toPixel: (value: number) => number,
): number | null {
  let nearestId: number | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (const line of chart.lines) {
    if (dayIndex < 0 || dayIndex >= line.values.length) continue;
    const value = line.values[dayIndex];
    if (value === null) continue;
    const distance = Math.abs(toPixel(value) - pointerY);
    if (distance < nearestDistance) {
      nearestId = line.id;
      nearestDistance = distance;
    }
  }
  return nearestId;
}

/** The narrowest zoom, in days between its first and last point. */
const MIN_ZOOM_DAYS = 1;

/** The library's own narrowest zoom, as a percentage of the axis. */
const DEFAULT_MIN_ZOOM_SPAN = 10;

/**
 * The narrowest zoom as a percentage of the axis. The library's default of 10%
 * meant a year could not be zoomed below about a month, so a drag inside an
 * already-zoomed range was pushed back out to that width.
 */
export function minZoomSpan(dayCount: number): number {
  if (dayCount < 2) return DEFAULT_MIN_ZOOM_SPAN;
  return Math.min(
    DEFAULT_MIN_ZOOM_SPAN,
    (100 * MIN_ZOOM_DAYS) / (dayCount - 1),
  );
}

/**
 * The zoom moved out to the days it covers, so the chart draws exactly the days
 * Net Change and the date box use. A drag lands a few pixels either side of a
 * day's point, which left that day out of the drawing although it was counted.
 * A zoom that already covers everything is left alone.
 */
export function snapZoomToDays(
  days: readonly Dayjs[],
  zoom: { start: number; end: number },
): { start: number; end: number } {
  const window = zoomWindow(days, zoom);
  if (!window) return zoom;
  const first = days[0].valueOf();
  const span = days[days.length - 1].valueOf() - first;
  if (span <= 0) return zoom;
  return {
    start: ((window.start.valueOf() - first) / span) * 100,
    end: ((window.end.valueOf() - first) / span) * 100,
  };
}
